import { formatearFecha } from '../../../nucleo/fecha/fechaLocal.js';
import { CalendarioPlegadoPorAnio } from '../../msi/componentes/CalendarioPlegadoPorAnio.jsx';
import {
  importe as formatearImporte,
  NOMBRE_DEL_ESTADO_DE_PAGO,
  resumirElAnioDelPrestamo,
  SUSTANTIVO_DEL_PRESTAMO,
} from '../formato.js';

/**
 * EL CALENDARIO DE PAGOS DEL PRÉSTAMO (RF-270, RN-776, D-455).
 *
 * AQUÍ NO HAY UNA SEGUNDA IMPLEMENTACIÓN DEL PLIEGUE, y es regla dura: el pliegue
 * por año lo pone `CalendarioPlegadoPorAnio.jsx`, la pieza que construyó el
 * incremento 22, consumida por IMPORT DIRECTO desde el módulo de meses sin
 * intereses. Ni se copia, ni se reimplementa, ni se traslada al núcleo —legítimo
 * porque el plan de préstamo ES un plan de esa maquinaria (D-432), y es el mismo
 * criterio con el que el panel consume la pieza del contador (RN-718)—. Una segunda
 * implementación de pliegue en cualquier archivo del 20 es defecto de construcción
 * aunque se vea igual (mismo carácter que RNF-138).
 *
 * LO ÚNICO QUE ESTE ENVOLTORIO APORTA son las palabras del préstamo: su renglón de
 * año habla de PAGOS y su estado agregado se dice «Todos por pagar» / «2 pagados ·
 * 10 por pagar», porque en un préstamo «cargada» no es la palabra de la titular:
 * ella paga. Las dos propiedades son la ADENDA ADITIVA de RN-776 a la pieza; el
 * umbral, el año que abre, la ausencia de «abrir todos» y la falta de memoria entre
 * visitas no cambian ni una línea (RN-767, RN-771).
 *
 * PARTICULARIDADES DEL PRÉSTAMO que la pieza ya resuelve sin cambio:
 *   · «la última, $X» NO APARECE NUNCA: ninguna mensualidad absorbe residuo, todas
 *     valen el pago mensual declarado (D-432a).
 *   · NO HAY PAGOS HISTÓRICOS: solo nacen los que faltan (D-433), así que la
 *     distinción histórica no se usa y el aviso de los pagos anteriores lo pone la
 *     pantalla aparte, con el texto del servidor.
 */
export function CalendarioDelPrestamo({
  mensualidades,
  codigoISO,
  datoDePrueba = 'prestamo-calendario',
  mostrarEstadoAgregado = true,
}) {
  return (
    <CalendarioPlegadoPorAnio
      renglones={renglonesDelCalendario(mensualidades)}
      codigoISO={codigoISO}
      datoDePrueba={datoDePrueba}
      mostrarEstadoAgregado={mostrarEstadoAgregado}
      sustantivo={SUSTANTIVO_DEL_PRESTAMO}
      resumirEstadoAgregado={resumirElAnioDelPrestamo}
      pintarRenglon={(renglon) => {
        const pago = renglon.origen;

        return (
          <div className="fila" key={pago.numeroDeMensualidad} data-prueba={`${datoDePrueba}-pago-${pago.numeroDeMensualidad}`}>
            <div className="crece">
              {/* El contador viaja ARMADO del servidor (RNF-038): aquí no se
                  concatena el número, la barra y el plazo. Es la IDENTIDAD de este
                  pago —15/48—, no el avance del plan (RN-779). */}
              <b>{pago.contadorDeMensualidad}</b>
              <small>
                {formatearFecha(pago.fechaDePago)}
                {' · '}
                {pago.textoEtiquetaDelPeriodo ?? NOMBRE_DEL_ESTADO_DE_PAGO[pago.estadoPresentado] ?? pago.estadoPresentado}
                {pago.esLaQueSigue && (
                  <span className="chip" data-prueba={`${datoDePrueba}-el-que-sigue`}> el que sigue</span>
                )}
              </small>
            </div>
            <span className="imp">{formatearImporte(pago.importe, codigoISO)}</span>
          </div>
        );
      }}
    />
  );
}

/**
 * Los renglones que la pieza del pliegue necesita, con lo que YA VINO RESUELTO del
 * servidor: aquí no se compara ninguna fecha y no se cuenta ningún pago (RNF-059).
 *
 * `fechaCorte` es el nombre que la pieza usa para derivar el año del grupo; en un
 * préstamo la fecha de corte y la fecha de pago son LA MISMA (RN-742), así que se le
 * pasa la fecha de pago sin transformarla.
 *
 * `esHistorica` va SIEMPRE en falso y `esLaDelResiduo` también: en un préstamo no
 * nacen pagos del pasado (D-433) y ninguno absorbe centavos (D-432a).
 */
function renglonesDelCalendario(mensualidades) {
  return (mensualidades ?? []).map((pago) => ({
    clave: pago.idMensualidad ?? pago.numeroDeMensualidad,
    fechaCorte: pago.fechaDePago,
    importe: pago.importe,
    estaCargada: pago.estadoPresentado === 'CARGADA' || pago.estadoPresentado === 'LIQUIDADA',
    esHistorica: false,
    esLaQueSigue: Boolean(pago.esLaQueSigue),
    esLaDelResiduo: false,
    origen: pago,
  }));
}
